import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";

const posts = [
  {
    slug: "sunday-dips",
    category: "Community",
    date: "Latest",
    title: "Why we dip every Sunday at St Bees",
    excerpt: "Cold water, calming exercises, and tea and biscuits afterwards. How a free Sunday ritual became the heartbeat of the RX4 community.",
    img: "/images/rx4/dave-sea-dip.jpg",
    alt: "Dave mid-dip at St Bees with the community behind",
    read: "4 min read",
    featured: true,
  },
  {
    slug: "balanced-leader-launch",
    category: "Press",
    date: "Latest",
    title: "RX4 Lakes launches The Balanced Leader pathway",
    excerpt: "A four-stage leadership framework built on six principles, designed for leaders who want growth that fits who they are.",
    img: "/images/rx4/dave-summit.webp",
    alt: "Dave with arms raised on a fell at sunset",
    read: "3 min read",
  },
  {
    slug: "location-energy",
    category: "Journal",
    date: "Latest",
    title: "Location has energy, time has memory",
    excerpt: "Why we coach on fells and dip in the sea instead of sitting in meeting rooms, and what wild places do to a busy mind.",
    img: "/images/rx4/community-dip.webp",
    alt: "Community cold water dip in the lake",
    read: "5 min read",
  },
  {
    slug: "cold-water-leadership",
    category: "Journal",
    date: "Latest",
    title: "What cold water taught me about leadership",
    excerpt: "The first thirty seconds in the lake are pure honesty. Dave on breath, composure and making hard decisions under pressure.",
    img: "/images/rx4/dave-ennerdale-bag.webp",
    alt: "Dave standing by Ennerdale lake",
    read: "6 min read",
  },
  {
    slug: "habit-hacking",
    category: "Journal",
    date: "Latest",
    title: "Habit hacking: routines that actually stick",
    excerpt: "Notes from an RX4 workshop on building daily structure that holds when life gets loud.",
    img: "/images/rx4/workshop-keynote.webp",
    alt: "Dave delivering an RX4 workshop",
    read: "4 min read",
  },
];

const Journal = () => {
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <main data-testid="journal-page" className="pt-32">
      <section className="section-shell pt-8 md:pt-12" data-testid="journal-section">
        <Reveal className="mb-14 md:mb-20">
          <p className="overline-label mb-4">Journal & Press</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-bone leading-[1.05]" data-testid="journal-headline">
            <MaskedLine delay={0.1}>Stories from</MaskedLine>
            <MaskedLine delay={0.25}>the <span className="text-brand">water's edge.</span></MaskedLine>
          </h1>
          <p className="mt-6 text-slate-400 max-w-xl">Field notes, press updates and lessons from the fells, the lakes and the sea.</p>
        </Reveal>

        {/* FEATURED */}
        <Reveal>
          <article data-testid="journal-featured" className="group grid grid-cols-1 lg:grid-cols-12 gap-8 bg-ink-soft border border-white/10 rounded-2xl overflow-hidden mb-12 transition-colors duration-300 hover:border-brand/40">
            <div className="lg:col-span-7 overflow-hidden">
              <img src={featured.img} alt={featured.alt} className="w-full h-full min-h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            </div>
            <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-brand/10 border border-brand/30 rounded-full text-brand">{featured.category}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{featured.read}</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-bone leading-snug group-hover:text-brand transition-colors duration-300">{featured.title}</h2>
              <p className="mt-4 text-slate-400 leading-relaxed">{featured.excerpt}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-brand">
                Read the story <ArrowRight size={14} />
              </span>
            </div>
          </article>
        </Reveal>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <article data-testid={`journal-card-${p.slug}`} className="group bg-ink-soft border border-white/10 rounded-2xl overflow-hidden h-full transition-colors duration-300 hover:border-brand/40">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={p.img} alt={p.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-black/60 backdrop-blur rounded-full text-slate-200 border border-white/10">{p.category}</span>
                </div>
                <div className="p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">{p.read}</p>
                  <h3 className="font-display text-xl font-semibold tracking-tight text-bone mb-3 group-hover:text-brand transition-colors duration-300">{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.excerpt}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="bg-ink-soft border-t border-white/5" data-testid="journal-cta">
        <div className="section-shell text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-bone max-w-2xl mx-auto leading-[1.1]">
              New stories land in the mailing list first.
            </h2>
            <p className="mt-6 text-slate-400 max-w-xl mx-auto">Join below in the footer, or start a conversation with Dave directly.</p>
            <Link to="/contact" data-testid="journal-contact-btn" className="btn-primary group mt-10">
              Get in touch
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Journal;
